/** Animated inhabitants of player-built settlements. Records arrive as a
 * compact public projection; transforms are analytic from world time and the
 * shared homestead voxel path, so there is no resident transform stream. */
import * as THREE from "three";
import {
  directionDayFrac,
  hashSeed,
  homesteadWalkPath,
  legacyProfessionSpecialty,
  settlementResidentPose,
  settlementResidentStyle,
  type HomesteadRoomSolution,
  type PlacedPiece,
  type SettlementPublicV1,
  type SettlementPublicResidentV1,
} from "@hyperlight/shared";
import { Avatar } from "../character/avatar";
import { driveAvatar, type AvatarDriveClock } from "../character/avatarDriver";
import type { StarSystem } from "./starsystem";
import type { HomesteadPieces } from "./homesteadPieces";

/** Each profession works its own trade (UAL 1+2 loops, 2026-08-14): mining
 * swings double as smith hammering, the spell idles are Materia channeling,
 * counter clips serve the traders. Unknown professions fall back to repairs. */
const WORK_EMOTES: Readonly<Record<string, string>> = {
  miner: "emo_mine",
  weaponsmith: "emo_mine",
  salvager: "emo_pickup",
  synthesist: "emo_channel",
  combiner: "emo_channel2",
  chef: "emo_chop",
  metallurgist: "emo_fixing",
  fabricator: "emo_fixing",
  engineer: "emo_fixing",
  shipwright: "emo_fixing",
  chemist: "emo_fixing",
  "fuel-technician": "emo_fixing",
  outfitter: "emo_counter_show",
  liaison: "emo_counter_show",
  merchant: "emo_counter_idle",
  broker: "emo_counter_idle",
  quartermaster: "emo_counter_idle",
  steward: "emo_talk",
};
/** Farmers split deterministically across the four farm motions, so a busy
 * Biogenerator reads as a crew at different chores, not synchronized mimes. */
const FARM_WORK = ["emo_farm_plant", "emo_farm_water", "emo_farm_harvest", "emo_farm_scatter"] as const;

function residentWorkEmote(resident: SettlementPublicResidentV1): string {
  const profession = legacyProfessionSpecialty(resident.profession);
  if (profession === "farmer") return FARM_WORK[hashSeed(`${resident.id}:farmwork`) % FARM_WORK.length]!;
  return WORK_EMOTES[profession ?? ""] ?? "emo_fixing";
}

const SPAWN_M = 260;
const DROP_M = 320;
const SHADOW_M = 55;
const SLEEP_LIFT_M = 0.39;
const ROUTE_BLEND_MIN_S = 0.2;
const ROUTE_BLEND_MAX_S = 0.85;
const ROUTE_BLEND_MPS = 4;

interface ResidentView {
  beaconId: string;
  resident: SettlementPublicResidentV1;
  avatar: Avatar;
  solution: HomesteadRoomSolution;
  home: PlacedPiece;
  work: PlacedPiece;
  path: ReturnType<typeof homesteadWalkPath>;
  commons: PlacedPiece | null;
  commonsStand: [number, number, number] | null;
  workToCommons: ReturnType<typeof homesteadWalkPath>;
  commonsToHome: ReturnType<typeof homesteadWalkPath>;
  lastLocal: THREE.Vector3;
  planetId: string;
  animationClock: AvatarDriveClock;
  phase: number;
  asleep: boolean;
  pathKey: string;
  /** Cosmetic continuity only. Simulation/interaction still use the analytic
   * pose; this prevents a rebuilt route visibly snapping an existing view. */
  blendFrom: THREE.Vector3 | null;
  blendElapsedS: number;
  blendDurationS: number;
}

function phaseOf(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return h >>> 0;
}

function qRot(q: readonly [number, number, number, number], v: readonly [number, number, number]): THREE.Vector3 {
  return new THREE.Vector3(...v).applyQuaternion(new THREE.Quaternion(...q));
}

/** Furniture origins sit inside their meshes. Daily walking anchors stand on
 * the authored +z interaction side; sleep alone uses the bed centre. */
function standAnchor(piece: PlacedPiece): [number, number, number] {
  const offset = qRot(piece.q, [0, 0, 0.85]);
  return [piece.p[0] + offset.x, piece.p[1] + offset.y, piece.p[2] + offset.z];
}

/** Spread residents around a Commons table deterministically instead of
 * stacking every avatar on its function anchor. */
function commonsAnchor(piece: PlacedPiece, residentId: string): [number, number, number] {
  const positions: readonly [number, number, number][] = [
    [0.95, 0, 0], [-0.95, 0, 0], [0, 0, 0.85], [0, 0, -0.85],
  ];
  const offset = qRot(piece.q, positions[phaseOf(residentId) % positions.length]!);
  return [piece.p[0] + offset.x, piece.p[1] + offset.y, piece.p[2] + offset.z];
}

function commonsPiece(site: ReturnType<HomesteadPieces["residentSiteOf"]>,
  residentId: string, stage: SettlementPublicV1["stage"]): PlacedPiece | null {
  if (!site || stage < 1) return null;
  const pids = site.rooms.rooms.flatMap((room) => room.functionSlots.commons).sort((a, b) => a - b);
  if (pids.length === 0) return null;
  const pid = pids[phaseOf(`${residentId}:commons-room`) % pids.length];
  return site.pieces.find((piece) => piece.pid === pid) ?? null;
}

function routeKey(path: ReturnType<typeof homesteadWalkPath>): string {
  if (!path) return "blocked";
  return path.points.map((point) => point.map((n) => Math.round(n * 100)).join(",")).join(";");
}

function itineraryKey(homeWork: ReturnType<typeof homesteadWalkPath>,
  workCommons: ReturnType<typeof homesteadWalkPath>,
  commonsHome: ReturnType<typeof homesteadWalkPath>): string {
  return `${routeKey(homeWork)}|${routeKey(workCommons)}|${routeKey(commonsHome)}`;
}

export class HomesteadResidentLife {
  private settlements = new Map<string, SettlementPublicV1>();
  private views = new Map<string, ResidentView>();
  private frame = 0;
  private local = new THREE.Vector3();
  private world = new THREE.Vector3();
  private up = new THREE.Vector3();
  private forward = new THREE.Vector3();
  private matrix = new THREE.Matrix4();

  constructor(private scene: THREE.Scene) {}

  apply(records: readonly SettlementPublicV1[], full: boolean): void {
    if (full) this.settlements.clear();
    for (const record of records) {
      if (record.residents.length === 0 && record.facilities.length === 0) this.settlements.delete(record.beaconId);
      else this.settlements.set(record.beaconId, record);
    }
  }

  /** Owner-only full state can seed the public projection before its room
   * broadcast loops back, keeping panel actions visually immediate. */
  applyOwner(record: SettlementPublicV1): void { this.apply([record], false); }

  update(dt: number, cameraRender: THREE.Vector3, system: StarSystem,
    homesteads: HomesteadPieces | null, tSec: number): void {
    this.frame++;
    const live = new Set<string>();
    if (!homesteads) { this.dropAll(); return; }
    for (const [beaconId, settlement] of this.settlements) {
      const site = homesteads.residentSiteOf(beaconId);
      if (!site) continue;
      const planetIndex = system.planets.findIndex((planet) => planet.def.id === site.planetId);
      const planet = system.planets[planetIndex];
      if (!planet) continue;
      for (const resident of settlement.residents) {
        if (resident.homePid === null || resident.workplacePid === null) continue;
        const home = site.pieces.find((piece) => piece.pid === resident.homePid);
        const work = site.pieces.find((piece) => piece.pid === resident.workplacePid);
        if (!home || !work) continue;
        const commons = commonsPiece(site, resident.id, settlement.stage);
        const commonsStand = commons ? commonsAnchor(commons, resident.id) : null;
        this.world.set(...home.p).applyQuaternion(planet.group.quaternion).add(planet.group.position);
        const distance = this.world.distanceTo(cameraRender);
        let view = this.views.get(resident.id);
        if (!view && distance > SPAWN_M) continue;
        if (view && distance > DROP_M) { this.drop(resident.id); continue; }
        if (!view) {
          const avatar = new Avatar(this.scene, { sharedMaterials: true });
          avatar.setAmbient(true);
          avatar.setStyle(settlementResidentStyle(resident.id));
          avatar.setWeapon(null);
          avatar.footIkEnabled = false;
          const path = homesteadWalkPath(site.rooms, standAnchor(home), standAnchor(work));
          const workToCommons = commonsStand
            ? homesteadWalkPath(site.rooms, standAnchor(work), commonsStand) : null;
          const commonsToHome = commonsStand
            ? homesteadWalkPath(site.rooms, commonsStand, standAnchor(home)) : null;
          view = {
            beaconId, resident, avatar, solution: site.rooms, home, work,
            path, commons, commonsStand, workToCommons, commonsToHome,
            pathKey: itineraryKey(path, workToCommons, commonsToHome),
            lastLocal: new THREE.Vector3(), planetId: site.planetId,
            animationClock: { elapsed: 0 }, phase: phaseOf(resident.id), asleep: false,
            blendFrom: null, blendElapsedS: 0, blendDurationS: 0,
          };
          this.views.set(resident.id, view);
        } else if (view.solution !== site.rooms || view.home.pid !== home.pid || view.work.pid !== work.pid
          || view.commons?.pid !== commons?.pid) {
          const oldPath = view.path;
          const nextPath = homesteadWalkPath(site.rooms, standAnchor(home), standAnchor(work));
          const nextWorkToCommons = commonsStand
            ? homesteadWalkPath(site.rooms, standAnchor(work), commonsStand) : null;
          const nextCommonsToHome = commonsStand
            ? homesteadWalkPath(site.rooms, commonsStand, standAnchor(home)) : null;
          const nextKey = itineraryKey(nextPath, nextWorkToCommons, nextCommonsToHome);
          if (oldPath && nextPath && nextKey !== view.pathKey && view.lastLocal.lengthSq() > 1) {
            view.blendFrom = view.lastLocal.clone();
            view.blendElapsedS = 0;
            const dx = nextPath.points[0][0] - view.lastLocal.x;
            const dy = nextPath.points[0][1] - view.lastLocal.y;
            const dz = nextPath.points[0][2] - view.lastLocal.z;
            view.blendDurationS = Math.min(ROUTE_BLEND_MAX_S, Math.max(ROUTE_BLEND_MIN_S,
              Math.sqrt(dx * dx + dy * dy + dz * dz) / ROUTE_BLEND_MPS));
          }
          view.solution = site.rooms;
          view.home = home;
          view.work = work;
          view.path = nextPath;
          view.commons = commons;
          view.commonsStand = commonsStand;
          view.workToCommons = nextWorkToCommons;
          view.commonsToHome = nextCommonsToHome;
          view.pathKey = nextKey;
        }
        view.resident = resident;
        view.beaconId = beaconId;
        view.planetId = site.planetId;
        live.add(resident.id);

        const dayFrac = directionDayFrac(system.def, planetIndex, home.p, tSec);
        const homeStand = standAnchor(home);
        const routeHome = view.path?.points[0] ?? homeStand;
        const routeWork = view.path?.points[view.path.points.length - 1] ?? homeStand;
        const pose = settlementResidentPose(resident.id, dayFrac, planet.def.spin.periodS,
          routeHome, routeWork, view.path,
          view.commonsStand && view.workToCommons && view.commonsToHome ? {
            commons: view.commonsStand,
            workToCommons: view.workToCommons,
            commonsToHome: view.commonsToHome,
          } : null,
          resident.trait);
        const sleeping = pose.activity === "sleep";
        this.local.set(...(sleeping ? home.p : pose.position));
        if (sleeping) {
          this.local.addScaledVector(this.up.copy(this.local).normalize(), SLEEP_LIFT_M);
        } else {
          const floor = homesteads.standRadiusAt(site.planetId, this.local.x, this.local.y, this.local.z);
          if (floor !== null) this.local.normalize().multiplyScalar(floor + 0.05);
        }
        if (view.blendFrom) {
          view.blendElapsedS += Math.max(0, dt);
          const raw = Math.min(1, view.blendElapsedS / Math.max(0.001, view.blendDurationS));
          const eased = raw * raw * (3 - 2 * raw);
          const fromRadius = view.blendFrom.length();
          const targetRadius = this.local.length();
          this.local.lerpVectors(view.blendFrom, this.local, eased);
          if (this.local.lengthSq() > 1) {
            this.local.normalize().multiplyScalar(fromRadius + (targetRadius - fromRadius) * eased);
          }
          if (raw >= 1) view.blendFrom = null;
        }
        view.lastLocal.copy(this.local);

        view.avatar.group.position.copy(this.local).applyQuaternion(planet.group.quaternion).add(planet.group.position);
        this.up.copy(this.local).normalize().applyQuaternion(planet.group.quaternion);
        if (pose.moving) this.forward.set(...pose.forward);
        else if (pose.activity === "socialize" && view.commons) {
          this.forward.set(view.commons.p[0] - this.local.x, view.commons.p[1] - this.local.y,
            view.commons.p[2] - this.local.z);
        } else {
          this.forward.copy(qRot((pose.activity === "work" ? work : home).q,
            sleeping ? [1, 0, 0] : [0, 0, -1]));
        }
        this.forward.applyQuaternion(planet.group.quaternion);
        this.forward.addScaledVector(this.up, -this.forward.dot(this.up));
        if (this.forward.lengthSq() > 1e-6) {
          this.forward.normalize();
          this.matrix.lookAt(new THREE.Vector3(), this.forward, this.up);
          view.avatar.group.quaternion.setFromRotationMatrix(this.matrix);
        }
        if (sleeping !== view.asleep) {
          view.avatar.faceRig.setEmote(sleeping ? "sleep" : null);
          view.asleep = sleeping;
        }
        view.avatar.setShadowsEnabled(distance < SHADOW_M);
        const facilityWorking = settlement.facilities.some((facility) => {
          const room = site.rooms.rooms.find((candidate) =>
            candidate.functionAnchors[facility.roomFunction] === facility.anchorPid);
          return facility.working
            && (resident.facilityId === undefined || resident.facilityId === facility.id)
            && resident.workplacePid !== null
            && (room?.functionSlots[facility.roomFunction].includes(resident.workplacePid) ?? false);
        });
        driveAvatar(view.avatar, {
          weaponId: null, jet: "none", aiming: false, airborne: false, crouch: false, blocking: false,
          moveFwd: pose.moving ? 1 : 0, moveRight: 0, climb: 0, pitch: 0,
          groundSpeed: pose.speed,
          emote: pose.moving ? "" : sleeping ? "emo_lay"
            : pose.activity === "socialize" ? "emo_talk"
              : pose.activity === "work" && facilityWorking ? residentWorkEmote(resident) : "emo_lookaround",
        }, dt, cameraRender, this.frame, undefined, view.phase, view.animationClock);
      }
    }
    for (const id of [...this.views.keys()]) if (!live.has(id)) this.drop(id);
  }

  observeHit(planetId: string, origin: THREE.Vector3, dir: THREE.Vector3, maxDist: number):
    { dist: number; title: string; subtitle: string; beaconId: string; residentId: string } | null {
    let best: { dist: number; title: string; subtitle: string; beaconId: string; residentId: string } | null = null;
    for (const view of this.views.values()) {
      if (view.planetId !== planetId) continue;
      const dx = view.lastLocal.x - origin.x, dy = view.lastLocal.y - origin.y, dz = view.lastLocal.z - origin.z;
      const t = dx * dir.x + dy * dir.y + dz * dir.z;
      if (t < 0.3 || t > maxDist || (best && t >= best.dist)) continue;
      const px = dx - dir.x * t, py = dy - dir.y * t, pz = dz - dir.z * t;
      if (px * px + py * py + pz * pz > 0.8 * 0.8) continue;
      best = {
        dist: t,
        title: view.resident.name,
        subtitle: `${view.resident.profession ?? "resident"} · rank ${view.resident.rank}`,
        beaconId: view.beaconId,
        residentId: view.resident.id,
      };
    }
    return best;
  }

  private drop(id: string): void {
    const view = this.views.get(id);
    if (!view) return;
    view.avatar.group.parent?.remove(view.avatar.group);
    view.avatar.dispose(this.scene);
    this.views.delete(id);
  }

  private dropAll(): void { for (const id of [...this.views.keys()]) this.drop(id); }

  dispose(): void {
    this.dropAll();
    this.settlements.clear();
  }
}
